/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useNavigate} from 'react-router-dom';
import {connect} from 'react-redux';
import moment from 'moment';
import Loader from '../../../components/Loader';
import EmptyState from '../../../components/EmptyState';
import TableLayout from '../../../components/TableLayout';
import PageLimit from '../../../components/PageLimit';
import Pagination from '../../../components/Pagination';
import SecondaryButton from '../../../components/SecondaryButton';
import PrimaryButton from '../../../components/PrimaryButton';
import FilterV2 from '../../../components/FilterV2';
import PageNumber from '../../../components/PageNumber';
import SettingsBilling from './index';
import * as Actions from '../../../store/actions';
import {corruptedFile} from '../../../assets/images/images';
import {renderServices} from '../../../helpers/renderServicies';
import {parseUrlParams} from '../../../helpers';
import {PageContext} from '../../../helpers/context';
import {useAllApps} from '../../../hooks/useApps';
import {useQuery} from 'react-query';
import {fetchApiLogsFilters} from '../../../requests/queries/dashboard';
import {shortenLargeNumbers} from '../../../helpers/shortenLargeNumbers';
import {dateRanges, formatDate} from 'helpers/getDateRange';

function BillingsTransactions({
  auth: {loading, userDetails, billingsApiCalls},
  apiLogs: {exportLoading},
  getBillingsApiCalls,
  exportApiLogs,
  getApiLogsFilters,
}) {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [openFilter, setOpenFilter] = useState(true);
  const {data: filters, isLoading} = useQuery(['filters'], fetchApiLogsFilters);
  const {setPageName} = useContext(PageContext);
  const {userApps} = useAllApps();

  useEffect(() => {
    setPageName('billings');
  }, [setPageName]);

  useEffect(() => {
    getApiLogsFilters();
  }, [getApiLogsFilters]);

  const apiParams = parseUrlParams();

  useEffect(() => {
    if (!window.location.search && Object.keys(apiParams).length === 0) {
      return;
    }

    const paramsForAPI = {
      ...apiParams,
      limit,
      page: currentPage,
    };
    getBillingsApiCalls(paramsForAPI);
  }, [currentPage, limit, getBillingsApiCalls, JSON.stringify(apiParams)]);

  useEffect(() => {
    if (!window.location.search) {
      const searchParams = new URLSearchParams();
      const {start, end} = dateRanges.seven_days();
      searchParams.set('date', 'seven_days');
      searchParams.set('start', start);
      searchParams.set('end', end);

      navigate(`?${searchParams.toString()}`, {replace: true});
    }
  }, []);

  const getAppName = useCallback(
    appId => userApps?.find(app => app.value === appId),
    [userApps],
  );

  const pageClick = useCallback(
    selected => {
      setCurrentPage(selected);
      const searchParams = new URLSearchParams(document.location.search);
      searchParams.set('page', selected);
      navigate(`?${searchParams.toString()}`);
    },
    [navigate],
  );

  const filterOptions = useMemo(() => {
    if (!isLoading && !filters) {
      return [];
    }

    const mapToOptionsArray = (items, valueKey = '_id') =>
      items?.map(item => ({
        value:
          item?.[valueKey] || item?.replace?.(/ /g, '_')?.toLowerCase() || item,
        label:
          typeof item === 'object'
            ? item.label || item.name || String(item)
            : String(item),
      })) || [];

    return [
      ...(userApps?.length > 0
        ? [
            {
              key: 'apps',
              label: 'Apps',
              type: 'select',
              options: userApps.map(app => ({
                value: app.value,
                label: app.label || app.name || String(app),
              })),
            },
          ]
        : []),
      {
        key: 'services',
        label: 'Services',
        type: 'select',
        options: mapToOptionsArray(filters?.services),
      },
    ];
  }, [filters, isLoading, userApps]);

  const handleFilterChange = useCallback(
    newFilters => {
      const filteredOptions = newFilters;
      if (filteredOptions.dateRange) {
        filteredOptions.start = formatDate(
          new Date(filteredOptions.dateRange.start),
        );
        filteredOptions.end = formatDate(
          new Date(filteredOptions.dateRange.end),
        );
        delete filteredOptions.dateRange;
      }
      if (filteredOptions.apps) {
        filteredOptions.apps = Array.isArray(filteredOptions.apps)
          ? filteredOptions.apps.join(',')
          : filteredOptions.apps;
      }
      if (filteredOptions.services) {
        filteredOptions.services = Array.isArray(filteredOptions.services)
          ? filteredOptions.services.join(',')
          : filteredOptions.services;
      }
      setCurrentPage(1);
      const searchParams = new URLSearchParams();
      Object.entries(filteredOptions).forEach(([key, value]) => {
        searchParams.set(key, value);
      });
      navigate(`?${searchParams.toString()}`);
      getBillingsApiCalls({page: 1, limit, ...filteredOptions});
    },
    [getBillingsApiCalls, limit, navigate],
  );

  const canExportLogs =
    userDetails?.permissions?.billingPermissions?.exportLogs;

  const isFiltered =
    !loading &&
    billingsApiCalls?.api_logs?.length === 0 &&
    Object.keys(parseUrlParams()).length > 2;

  return (
    <SettingsBilling breadCrumbText="API Calls">
      <section>
        <div className="flex items-center justify-end gap-2 flex-wrap py-2  mx-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
          <SecondaryButton
            buttonText="Filter"
            onClick={() => setOpenFilter(prev => !prev)}
          />
          <PrimaryButton
            loading={exportLoading}
            onClick={() => {
              const apiParams = parseUrlParams();
              apiParams.limit = limit;
              apiParams.priced = true;
              exportApiLogs(apiParams);
            }}
            buttonText="Export"
            disabled={!canExportLogs}
          />
        </div>

        <div className={`filter-slide-container ${openFilter ? 'open' : ''}`}>
          <FilterV2
            filters={filterOptions}
            onFilterChange={handleFilterChange}
            initialValues={apiParams}
          />
        </div>

        <TableLayout negativeMargins>
          <thead className="font-semibold uppercase">
            <tr className="bg-white80">
              <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>App name</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>Date time</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>Services</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>Cost</span>
                </div>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={5}>
                {loading ? (
                  <Loader height={45} />
                ) : !loading &&
                  (!billingsApiCalls ||
                    billingsApiCalls?.api_logs?.length === 0) ? (
                  isFiltered ? (
                    <EmptyState
                      body="This filter did not return any data, try using different values."
                      src={corruptedFile}
                      height={45}
                      noBtn
                      customBtn={
                        <button
                          onClick={() => {
                            navigate('');
                            setCurrentPage(1);
                            getBillingsApiCalls({limit, page: 1});
                          }}
                          className="text-brandBlue p-4 text-sm font-medium"
                        >
                          Update preferences
                        </button>
                      }
                    />
                  ) : (
                    <EmptyState
                      body="No data found"
                      height={45}
                      src={corruptedFile}
                      noBtn
                    />
                  )
                ) : null}
              </td>
            </tr>
          </tbody>
          <tbody className="text-sm bg-white">
            {!loading &&
              billingsApiCalls?.api_logs?.map((billing, i) => (
                <tr className="border-[0.5px] border-[#E4E3EC]" key={i}>
                  <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-sm text-body whitespace-nowrap">
                    {getAppName(billing?.app)?.label || '-'}
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                    {moment(billing?.createdAt).format(
                      'Do MMM, YYYY, hh:mmA',
                    ) || '-'}
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                    {renderServices(billing?.service) ||
                      billing?.service?.replaceAll('_', ' ') ||
                      '-'}
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                    {shortenLargeNumbers(
                      billing?.cost / 100 || 0,
                      userDetails?.company?.currency || 'NGN',
                      {},
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </TableLayout>

        {!loading && billingsApiCalls?.api_logs?.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
            <PageNumber
              currentPage={+currentPage}
              limit={limit}
              totalCount={billingsApiCalls?.totalRecords || 0}
            />
            <div className="flex items-center gap-5">
              <PageLimit
                onLimitChange={newLimit => {
                  setLimit(newLimit);
                  setCurrentPage(1);
                }}
                total={billingsApiCalls?.totalRecords}
                length={billingsApiCalls?.api_logs?.length}
                limit={limit}
              />
              <Pagination
                total={Math.ceil((billingsApiCalls?.totalRecords || 0) / limit)}
                current={+currentPage}
                onPageChange={activePage => {
                  pageClick(activePage);
                }}
              />
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4 sm:hidden">
          <SecondaryButton
            buttonText="Create payment limit"
            className="w-full"
          />
          <PrimaryButton buttonText="Top Up Wallet" className="w-full" />
        </div>
      </section>
    </SettingsBilling>
  );
}

export default connect(state => state, Actions)(BillingsTransactions);
